import { useCallback, useEffect, useRef } from 'react';
import { CUSTOM_MESSAGE_SELECTOR } from './emailPreviewIframeConstants';
import { computeCustomMessagePosition } from './emailPreviewIframeUtils';

interface UseEmailPreviewContainerInteractionsOptions {
  emailHtml: string | null;
  isInitialized: boolean;
  handleClick: (e: React.MouseEvent | MouseEvent) => void;
  onElementPosition?: (position: { top: number; left: number }) => void;
  templateHtml: string;
  initialCustomHtml: string;
}

export function useEmailPreviewContainerInteractions({
  emailHtml,
  isInitialized,
  handleClick,
  onElementPosition,
  templateHtml,
  initialCustomHtml,
}: UseEmailPreviewContainerInteractionsOptions) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const sendElementPosition = useCallback(() => {
    const element = document.getElementById('custom-message');
    if (!element) {
      return;
    }

    const position = computeCustomMessagePosition(element);
    if (onElementPosition) {
      onElementPosition(position);
    }
  }, [onElementPosition]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const element = document.getElementById('custom-message');
      if (!element) {
        return;
      }

      sendElementPosition();
      const container = element.closest('[style*="position"]') || document.body;
      container.addEventListener('scroll', sendElementPosition, true);
      window.addEventListener('resize', sendElementPosition);
    }, 1000);

    return () => {
      clearTimeout(timer);
      const container =
        document.getElementById('custom-message')?.closest('[style*="position"]') || document.body;
      container.removeEventListener('scroll', sendElementPosition, true);
      window.removeEventListener('resize', sendElementPosition);
    };
  }, [templateHtml, initialCustomHtml, sendElementPosition]);

  useEffect(() => {
    if (!emailHtml) {
      return;
    }

    let clickHandler: ((e: MouseEvent) => void) | null = null;
    let positionTimer: NodeJS.Timeout | null = null;
    let linkClickHandler: ((e: MouseEvent) => void) | null = null;

    const containerEl = containerRef.current;
    if (!containerEl) {
      return;
    }

    const timer = setTimeout(() => {
      const customMessageEl = containerEl.querySelector(
        CUSTOM_MESSAGE_SELECTOR,
      ) as HTMLElement | null;
      if (customMessageEl && !isInitialized) {
        clickHandler = (e: MouseEvent) => {
          handleClick(e);
        };
        customMessageEl.addEventListener('click', clickHandler);
        positionTimer = setTimeout(() => {
          sendElementPosition();
        }, 500);
      }

      const allLinks = containerEl.querySelectorAll('a');
      linkClickHandler = (e: MouseEvent): void => {
        const target = e.target as HTMLElement;
        const link = target.closest('a');
        if (link) {
          const customMessageEl = containerEl.querySelector(CUSTOM_MESSAGE_SELECTOR);
          if (customMessageEl && !customMessageEl.contains(link)) {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      };

      allLinks.forEach((link) => {
        const customMessageEl = containerEl.querySelector(CUSTOM_MESSAGE_SELECTOR);
        if (customMessageEl && !customMessageEl.contains(link)) {
          link.style.pointerEvents = 'none';
          link.style.cursor = 'default';
          link.style.opacity = '0.7';
          if (linkClickHandler) {
            link.addEventListener('click', linkClickHandler, true);
          }
        }
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      if (positionTimer) {
        clearTimeout(positionTimer);
      }
      if (clickHandler) {
        const customMessageEl = containerEl.querySelector(CUSTOM_MESSAGE_SELECTOR);
        if (customMessageEl) {
          customMessageEl.removeEventListener('click', clickHandler as EventListener);
        }
      }
      if (linkClickHandler) {
        const allLinks = containerEl.querySelectorAll('a');
        allLinks.forEach((link) => {
          link.removeEventListener('click', linkClickHandler!, true);
        });
      }
    };
  }, [emailHtml, handleClick, isInitialized, sendElementPosition]);

  const handleContainerClick = useCallback(
    (e: React.MouseEvent) => {
      if (isInitialized) {
        return;
      }
      const target = e.target as HTMLElement;
      if (target.closest(CUSTOM_MESSAGE_SELECTOR)) {
        if (
          target.closest('.email-preview-editor') ||
          target.closest('.email-preview-editor-toolbar')
        ) {
          return;
        }
        handleClick(e);
      }
    },
    [handleClick, isInitialized],
  );

  return {
    containerRef,
    handleContainerClick,
  };
}
